import { ArrowPathIcon, BugAntIcon, CloudArrowDownIcon } from '@heroicons/react/24/outline'
import { isAfter, subDays } from 'date-fns'
import format from 'date-fns/format'
import { get, omit, startCase } from 'lodash'
import { ClassAttributes, ReactNode, SetStateAction, TdHTMLAttributes, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useMutation, useQueries, useQuery, useQueryClient, UseQueryOptions } from 'react-query'
import { useHistory, useParams } from 'react-router-dom'
import styled from 'styled-components'

import { saveCsv } from '@/components/BulkUpload/util/csv'
import InlineLoader from '@/components/InlineLoader'
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner'
import PageWithGrid from '@/components/PageWithGrid'
import PaginationTable from '@/components/PaginationTable'
import Breadcrumbs from '@/components/TailwindUI/Breadcrumbs'
import DateDayPicker from '@/components/TailwindUI/DayPicker'
import Skeleton from '@/components/TailwindUI/Skeleton'
import Toggle from '@/components/TailwindUI/Toggle'
import useFeatures from '@/components/useFeatures'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'
import { Header, StyledRow } from '@/pages/SpaceAppBuilder/AppDetailsPage/AppDetailsPage'
import { Action, Actions } from '@/pages/SpaceAppBuilder/components/StyledComponents'
import { triggersDescriptionAndIcon } from '@/pages/SpaceAppBuilder/FeaturesPage/featureToVisualWorkflow'
import { getScheduleText } from '@/pages/SpaceAppBuilder/FeaturesPage/Operations/Triggers/ScheduledTrigger/utils'
import {
  App,
  AppInstanceDataSource,
  Feature,
  HistoryRecordType,
  TriggerTypes,
  WorkgridCatalogDatasource
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import {
  dataSourceV3CatalogQueryOptions,
  featureQueryOptions,
  getFetchAppQueryOptions,
  getFetchHistoryQueryOptions
} from '@/pages/SpaceAppBuilder/utilities/queries'
import clientLocalStorage from '@/utilities/clientLocalStorage'
import cn from '@/utilities/cn'
import MarkdownParser from '@/utilities/markdownProcessors/MarkdownProcessor'

import columns from './columns'
import DebugModal from './DebugModal'
import messages from './messages'

const StyledPaginationTable = styled(PaginationTable)`
  padding-left: 48px;

  tr:hover {
    /* TODO Remove the automatic hover of the flat-table */
    background-color: var(--wg-background) !important;
  }

  th {
    font-size: 12px;
    text-align: left;

    &:first-child {
      padding-left: 0;
    }

    .sort-value {
      color: #4b4b4b;
      margin-top: 1px;
    }
  }

  td {
    font-size: 14px;
    border-bottom-color: rgb(231, 231, 231);
    border-bottom-width: 1px;
    border-bottom-style: solid;

    &:first-child {
      padding-left: 0;
    }
  }
`

const StyledFeatureName = styled.h2`
  font-weight: bold;
  justify-self: flex-start;
  font-size: 30px;
  color: #4b4b4b;
`

const LoadingContainer = styled.div`
  display: grid;
  align-items: center;
  justify-items: center;
  margin-top: 15px;
`

const HistoryPage = () => {
  const intl = useIntl()

  const { spaceId, appId, featureId } = useParams<{ spaceId: string; appId: string; featureId: string }>()
  const history = useHistory()
  const previousSpaceId = useRef(spaceId)
  const featureQueryResult = useQuery<Feature>(featureQueryOptions({ spaceId, appId, featureId }))
  const [selectedDay, setSelectedDay] = useState<Date>(() =>
    isAfter(new Date(clientLocalStorage.getItem('historySelectedDay')), subDays(new Date(), 7))
      ? new Date(clientLocalStorage.getItem('historySelectedDay'))
      : new Date()
  )
  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]

  const [isDebugModalOpen, setIsDebugModalOpen] = useState<boolean>(false)
  const [errorsOnly, setErrorsOnly] = useState<boolean>(clientLocalStorage.getItem('historyErrorsOny') || false)
  const featureMutation = useMutation({
    onSuccess: () => {
      // Reload the features
      featureQueryResult.refetch()
    }
  })

  useEffect(() => {
    if (previousSpaceId.current !== spaceId) {
      history.push(`/space/${spaceId}/appbuilder`)
    }
  }, [spaceId, history])

  const queryClient = useQueryClient()
  const appQueryResult = useQuery<App>(getFetchAppQueryOptions({ spaceId, appId })) //refetchInterval?

  const dataSources = appQueryResult?.data?.dataSources

  type WorkgridCatalogDatasourceQueries = UseQueryOptions<WorkgridCatalogDatasource>[]
  const workgridDatasourceConfigurationQueryResult = useQueries<WorkgridCatalogDatasourceQueries>(
    dataSources?.map((ds: AppInstanceDataSource) => ({
      ...dataSourceV3CatalogQueryOptions({
        spaceId,
        datasourceId: ds.datasourceId || ds.key
      }),
      enabled: !!dataSources
    })) || []
  )

  const fromDate = () => {
    // Using formating instead of toISOString to avoid timezone issues
    return format(new Date(selectedDay), `yyyy-MM-dd'T00:00:00:000Z'`)
  }
  const historyQueryResult = useQuery<unknown, any>({
    ...getFetchHistoryQueryOptions({ spaceId, appId, featureId, from: fromDate() }),
    enabled: !!featureQueryResult.data?.id
  })

  const exportHistory = (executionData: HistoryRecordType[]) => {
    const allColumnHeaders = Object.keys(Object.assign({}, ...executionData))
    saveCsv(executionData, `${featureQueryResult.data?.name}History.csv`, allColumnHeaders)
  }

  const onDebugLoggingChange = async (reset?: boolean) => {
    const debugLogging = reset ? true : !featureQueryResult.data?.isDebugLoggingEnabled
    const feature = omit(featureQueryResult?.data, [
      'id',
      'workflow',
      'createdBy',
      'modifiedBy',
      'createdDate',
      'modifiedDate',
      'audience',
      'templateFeatureId'
    ])
    // @ts-expect-error
    await featureMutation.mutateAsync([
      featureQueryOptions({
        spaceId,
        appId,
        featureId
      }).queryKey[0],
      {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          ...feature,
          isDebugLoggingEnabled: debugLogging
        })
      }
    ])
  }

  const renderFeatureTriggerMessage = (feature: { workflow: { trigger: any } }) => {
    const { trigger } = feature.workflow
    if (trigger) {
      switch (trigger.type) {
        case TriggerTypes.none: {
          return <>A trigger has not been selected for this {agentsFlagOn ? 'capability' : 'feature'}.</>
        }
        case TriggerTypes.manual: {
          return (
            <>
              On the {agentsFlagOn ? 'capability' : 'feature'} page, click the <strong>Run</strong> button to trigger
              this {agentsFlagOn ? 'capability' : 'feature'} and produce operation logs.
            </>
          )
        }
        case TriggerTypes.dataSourceEvent: {
          const workgridDataSource = workgridDatasourceConfigurationQueryResult.find(
            workgridDs => workgridDs.data?.id === trigger.triggerData?.triggerSourceKey
          )
          return (
            <>
              Your {agentsFlagOn ? 'capability' : 'feature'} is subscribed to{' '}
              <strong>{trigger.triggerData.triggerEvent}</strong>events from{' '}
              <strong>{workgridDataSource?.data?.name}</strong>.
            </>
          )
        }
        case TriggerTypes.scheduled: {
          if (!trigger.triggerData) {
            return <>No schedule has been specified for this {agentsFlagOn ? 'capability' : 'feature'}.</>
          }
          return <>{MarkdownParser(`${getScheduleText(trigger, intl, true)}`)}</>
        }
        default: {
          return `${startCase(trigger.type)} trigger`
        }
      }
    }
  }

  const [currentPage, setCurrentPage] = useState(1)

  const refreshFeatureHistory = () => {
    queryClient.resetQueries(getFetchHistoryQueryOptions({ spaceId, appId, featureId, from: fromDate() }))
  }

  // table sort
  const defaultSort = {
    createdDate: {
      direction: 'desc',
      position: 0
    }
  }

  const CellWrapper = (
    cellProps: JSX.IntrinsicAttributes &
      ClassAttributes<HTMLTableDataCellElement> &
      TdHTMLAttributes<HTMLTableDataCellElement>
  ) => {
    return <td className="featureHistory-table__cell" {...cellProps} />
  }
  const RowWrapper = (props: any) => {
    return <tr {...props} />
  }
  RowWrapper.shouldComponentUpdate = () => {
    return true
  }

  const featureHistoryNotFound = historyQueryResult.status === 'error' && historyQueryResult.error?.status === 404

  const historyErrorMessage = featureHistoryNotFound
    ? `The ${agentsFlagOn ? 'capability' : 'feature'} has not been run yet.`
    : intl.formatMessage(messages.errorTitle)

  const historyErrorSubmessage = featureHistoryNotFound ? '' : intl.formatMessage(messages.errorMessage)
  return (
    <PageWithGrid>
      <>
        <Breadcrumbs
          className="flex px-12 py-3"
          breadcrumbs={[
            { label: `${agentsFlagOn ? 'Agents' : 'Apps'}`, path: `/space/${spaceId}/appbuilder` },
            {
              label: appQueryResult.data?.name || `${agentsFlagOn ? 'Agent' : 'App'}`,
              path: `/space/${spaceId}/appbuilder/${appId}`
            },
            {
              label: get(featureQueryResult, 'data.name', `${agentsFlagOn ? 'Capability' : 'Feature'}`),
              path: `/space/${spaceId}/appbuilder/${appId}/features/${featureId}`
            },
            {
              label: `${agentsFlagOn ? 'Capability' : 'Feature'} History`
            }
          ]}
        />

        <div className="m-auto max-w-screen-xl">
          <Header>
            <StyledRow>
              <div className="flex-column flex shrink-0 grow basis-5/12">
                <InlineLoader
                  useQueryResult={featureQueryResult}
                  loaderComponent={
                    <div className="flex">
                      <div>
                        <Skeleton className="mt-2 h-8 w-64" />
                        <Skeleton className="mt-1 h-6 w-96" />
                        <Skeleton className="mt-1 h-6 w-60" />
                      </div>
                    </div>
                  }
                >
                  {featureResult => {
                    const triggerData: {
                      icon?: ReactNode
                      iconUrl?: string
                      description?: { defaultMessage: string }
                    } = triggersDescriptionAndIcon(featureResult.workflow.trigger)
                    return (
                      <div>
                        <StyledFeatureName>{featureResult?.name} history</StyledFeatureName>
                        {triggerData && (
                          <div className="my-4 flex items-center gap-2 text-gray-900">
                            {/* eslint-disable-next-line unicorn/prefer-logical-operator-over-ternary */}
                            {triggerData.icon ? (
                              triggerData.icon
                            ) : triggerData.iconUrl ? (
                              <img className="h-5 w-5 rounded-md" src={triggerData.iconUrl} alt="data source icon" />
                            ) : null}
                            {renderFeatureTriggerMessage(featureResult)}
                          </div>
                        )}
                      </div>
                    )
                  }}
                </InlineLoader>
              </div>
              <div className="flex shrink-0 grow flex-row">
                <Actions>
                  <InlineLoader
                    useQueryResult={historyQueryResult}
                    loaderComponent={<Skeleton className="-mt-1 mr-8 h-16 w-12" />}
                  >
                    {(
                      featureExecutionHistory // Chat to Matt and Vince about this
                    ) => (
                      <Action
                        title="Save CSV file of history to your device"
                        onClick={() => exportHistory(featureExecutionHistory)}
                      >
                        <div className="relative -mt-1 w-min cursor-pointer rounded-full hover:border-zinc-200 hover:bg-zinc-50">
                          <svg width="48" height="48" viewBox="0 0 120 120">
                            <circle cx="60" cy="60" r="54" fill="none" stroke="#dcdcdc" strokeWidth="3" />
                          </svg>
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform">
                            <CloudArrowDownIcon className="h-[30px] w-[30px] text-blue-700" aria-hidden="true" />
                          </div>
                        </div>
                        {<span className="mt-[7px]">Download</span>}
                      </Action>
                    )}
                  </InlineLoader>
                  <InlineLoader
                    useQueryResult={featureQueryResult}
                    overloadIsLoading={featureMutation.isLoading && !isDebugModalOpen}
                    loaderComponent={<Skeleton className="-mt-1 mr-8 h-16 w-12" />}
                  >
                    {featureResult => (
                      <Action
                        onClick={() => setIsDebugModalOpen(true)}
                        title="Debug mode will capture logs for all events for 1 day"
                      >
                        <DebugModal
                          isOpen={isDebugModalOpen}
                          onDebugLoggingChange={onDebugLoggingChange}
                          onClose={() => setIsDebugModalOpen(false)}
                          isLoading={featureMutation.isLoading}
                          isError={featureMutation.isError}
                          isDebugLoggingEnabled={featureResult.isDebugLoggingEnabled}
                        />
                        <div
                          className={cn(
                            featureResult.isDebugLoggingEnabled
                              ? 'bg-blue-700 hover:bg-blue-500'
                              : 'hover:border-zinc-200 hover:bg-zinc-50',
                            'relative -mt-1 w-min cursor-pointer rounded-full'
                          )}
                        >
                          <svg width="48" height="48" viewBox="0 0 120 120">
                            <circle
                              cx="60"
                              cy="60"
                              r="54"
                              fill="none"
                              stroke={featureResult.isDebugLoggingEnabled ? 'rgb(29 78 216)' : '#dcdcdc'}
                              strokeWidth="3"
                            />
                          </svg>
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform">
                            <BugAntIcon
                              className={cn(
                                featureResult.isDebugLoggingEnabled ? 'text-white' : 'text-blue-700',
                                'h-[30px] w-[30px]'
                              )}
                              aria-hidden="true"
                            />
                          </div>
                        </div>
                        <span className="mt-[7px]">Debug: {featureResult.isDebugLoggingEnabled ? 'on' : 'off'}</span>
                      </Action>
                    )}
                  </InlineLoader>
                  <Action onClick={refreshFeatureHistory}>
                    <div className="relative -mt-1 w-min cursor-pointer rounded-full hover:border-zinc-200 hover:bg-zinc-50">
                      <svg width="48" height="48" viewBox="0 0 120 120">
                        <circle cx="60" cy="60" r="54" fill="none" stroke="#dcdcdc" strokeWidth="3" />
                      </svg>
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform">
                        <ArrowPathIcon className="h-[30px] w-[30px] text-blue-700" aria-hidden="true" />
                      </div>
                    </div>
                    {<span className="mt-[7px]">Refresh</span>}
                  </Action>
                </Actions>
              </div>
            </StyledRow>
            <StyledRow>
              <div className="flex-column flex shrink-0 grow basis-5/12">{/*TODO add countdown clock and modal*/}</div>
              <div className="flex shrink-0 grow flex-row items-center justify-end gap-2">
                <p className="font-medium text-gray-900">View runs since:</p>
                <DateDayPicker
                  fromDate={subDays(new Date(), 7)}
                  toDate={new Date()}
                  selected={selectedDay}
                  onSelectDay={day => {
                    clientLocalStorage.setItem('historySelectedDay', day)
                    setSelectedDay(day)
                  }}
                />
                <Toggle
                  className="ml-6 items-center"
                  onChange={() => {
                    clientLocalStorage.setItem('historyErrorsOny', !errorsOnly)
                    setErrorsOnly(!errorsOnly)
                  }}
                  checked={errorsOnly}
                />
                <span className={cn('text-sm font-medium text-gray-900')}>Failures only</span>
              </div>
            </StyledRow>
          </Header>
          <InlineLoader
            useQueryResult={historyQueryResult}
            overloadIsLoading={featureMutation.isLoading}
            errorOptions={{ message: historyErrorMessage, subMessage: historyErrorSubmessage }}
            loaderComponent={
              <LoadingContainer>
                <LoadingSpinner />
              </LoadingContainer>
            }
          >
            {(
              featureExecutionHistory // Chat to Matt and Vince about this
            ) =>
              featureExecutionHistory.length > 0 ? (
                <StyledPaginationTable
                  columns={columns({
                    intl
                  })}
                  onPaginateSelect={(page: SetStateAction<number>) => setCurrentPage(page)}
                  pagination={{
                    isClientPaging: true,
                    count: featureExecutionHistory.length,
                    startIndex: currentPage * 10,
                    perPage: 10
                  }}
                  renderers={{
                    body: {
                      row: RowWrapper,
                      cell: CellWrapper
                    }
                  }}
                  rowItemType=""
                  rows={
                    errorsOnly
                      ? featureExecutionHistory.filter((execution: { status: string }) => execution.status === 'failed')
                      : featureExecutionHistory
                  }
                  rowKey="_index"
                  sortingColumn={defaultSort}
                />
              ) : (
                <p className="text-medium mt-8 text-center font-bold">
                  There have been no {agentsFlagOn ? 'capability' : 'feature'} runs for the selected date range
                </p>
              )
            }
          </InlineLoader>
        </div>
      </>
    </PageWithGrid>
  )
}

HistoryPage.displayName = 'HistoryPage'

export default HistoryPage
